import { motion } from "framer-motion";
import { ProgressCircle } from "./ProgressCircle";
import { Card } from "../Card";
import { TrendingUp, ArrowRight } from "lucide-react";
import { Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import { getReadinessScore } from "./apis/readinessScore";
import Title from "antd/es/typography/Title";
import { LoadingOutlined } from "@ant-design/icons";
import "./styles.css";
import { StyledButton } from "Components/StyledButton";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Features/Routes/routeConstants";

const getScoreColor = (score) => {
  if (score >= 80) return "text-green-600";
  if (score >= 60) return "text-yellow-600";
  return "text-red-600";
};

const getScoreLabel = (score) => {
  if (score >= 80) return "Excellent";
  if (score >= 60) return "Good";
  return "Needs Work";
};

export const ReadinessScore = () => {
  const { data, isLoading, error } = useQuery(["getReadinessScore"], {
    queryFn: getReadinessScore,
  });
  const readinessScore = data?.data?.readinessScore || 0;
  const navigate = useNavigate();
  if (error) {
    return (
      <Title level={2}>{"There is an error, we will return shortly"}</Title>
    );
  }
  if (isLoading) {
    return <Spin indicator={<LoadingOutlined spin />} size="large" />;
  }
  return (
    <Card className="text-center w-[90%] h-[90%] mt-4" delay={0.1}>
      <div className="flex flex-col md:flex-row items-center justify-between w-full p-2 md:p-4">
        <div className="flex-1">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center justify-center mb-6 md:mb-0"
          >
            <ProgressCircle percentage={readinessScore} size="lg">
              <div className="text-center">
                <div
                  className={`text-2xl font-bold ${getScoreColor(
                    readinessScore
                  )}`}
                >
                  {readinessScore}%
                </div>
                <div className="text-base text-gray-600">Ready</div>
              </div>
            </ProgressCircle>
          </motion.div>
        </div>

        <div className="flex-1 md:ml-8">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-primary-500 mr-2" />
              <h3 className="text-xl font-bold text-gray-900 mb-0">
                Readiness Calculator
              </h3>
            </div>

            <p className="text-gray-600 mb-4 text-base font-medium">
              {readinessScore >= 60
                ? `Your readiness score is `
                : `Your current readiness score `}
              <span
                className={`font-semibold ${getScoreColor(readinessScore)}`}
              >
                {getScoreLabel(readinessScore)}
              </span>
            </p>

            <p className="text-base text-gray-500 mb-6 font-medium">
              Based on your profile completion, test scores, and application
              materials
            </p>

            {readinessScore < 100 && (
              <StyledButton
                type="primary"
                className="w-full md:w-auto h-fit"
                onClick={() => {
                  navigate(ROUTES.READINESS_CALCULATOR);
                }}
              >
                <div className="flex items-center p-2 font-medium text-base">
                  Improve Readiness Score
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </StyledButton>
            )}
          </motion.div>
        </div>
      </div>
    </Card>
  );
};
